import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { addDays, format, startOfDay, subDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import DateField from 'common/forms/DateField';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import { rescheduleFillLaterItem } from 'store/thunks/workQueueThunks';

import { formatScheduledDate } from '../utils/fillLaterDates';

/**
 * "Change the date" for a Fill Later item. Same shape as ShipLaterButton's dialog: a
 * react-final-form DateField whose earliest selectable day is tomorrow, with no upper bound -
 * the date is the user's.
 */
const RescheduleFillLaterDialog = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const earliestSelectableDate = addDays(startOfDay(new Date()), 1);
  // Lifted out of the Form render prop so the Dialog's close handler can see it: while the save
  // is in flight no close reason (backdrop, Escape) may dismiss the dialog, exactly as in
  // CreateFillLaterNowDialog. The table stays unreachable until the mutation has settled.
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSubmit = async ({ scheduledFor }, form) => {
    if (!scheduledFor) return;
    setSubmitting(true);
    try {
      const updated = await dispatch(
        rescheduleFillLaterItem({
          fillLaterItemId: item.fillLaterItemId,
          scheduledFor: format(scheduledFor, 'yyyy-MM-dd'),
        })
      );
      if (!updated) return;
      enqueueSnackbar(`Scheduled date updated to ${format(scheduledFor, 'MM-dd-yyyy')}`, {
        variant: 'success',
      });
      form.reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <Form
        onSubmit={handleSubmit}
        render={({ handleSubmit: submitForm, values }) => (
          <form noValidate onSubmit={submitForm}>
            <DialogTitle>Change the scheduled date</DialogTitle>
            <DialogContent>
              <Typography variant='body2' sx={{ mb: 0.5 }}>
                {item.patientName} · {formatDrugLabel(item)} · Qty {item.splitQuantity}
              </Typography>
              <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
                Order {item.referenceOrderId} · currently scheduled for{' '}
                {formatScheduledDate(item.scheduledFor)}
              </Typography>
              <DateField
                name='scheduledFor'
                id='RescheduleFillLaterDialog-scheduledFor'
                label='New scheduled date'
                // Tomorrow onwards, written the same way as the patient profile's split dialog:
                // validateMinDate is a strict isAfter, so the bound sits a day before the window
                // and shouldDisableDate greys out that extra day.
                minDate={subDays(earliestSelectableDate, 1)}
                shouldDisableDate={(date) => startOfDay(date) < earliestSelectableDate}
                required
              />
              {item.status === 'WAITING_SHIPMENT' && (
                <Alert severity='info' sx={{ mt: 2 }}>
                  This item is waiting on shipment. Moving it to a future date returns it to
                  Scheduled; it will wait for both the new date and the original shipping.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' color='secondary' onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <LoadingButton
                variant='contained'
                color='primary'
                loading={submitting}
                disabled={!values.scheduledFor}
                type='submit'
              >
                Save date
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

RescheduleFillLaterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    fillLaterItemId: PropTypes.string.isRequired,
    referenceOrderId: PropTypes.string,
    patientName: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    splitQuantity: PropTypes.string,
    scheduledFor: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default RescheduleFillLaterDialog;
